import { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Sun, Moon, Bell, ChevronDown, KeyRound, LogOut, UserCircle } from 'lucide-react'
import { useTheme } from '../theme/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import GlobalSearch from './GlobalSearch'

const titleMap: Record<string, { label: string; gradient: string }> = {
  '/dashboard': { label: 'Dashboard', gradient: 'gradient-text-toga' },
  '/inbox': { label: 'Buzón', gradient: 'gradient-text-blue' },
  '/clients': { label: 'Clientes', gradient: 'gradient-text-purple' },
  '/clients/import': { label: 'Importar clientes', gradient: 'gradient-text-purple' },
  '/onboarding': { label: 'Primeros pasos', gradient: 'gradient-text-toga' },
  '/cases': { label: 'Expedientes', gradient: 'gradient-text-rose' },
  '/documents': { label: 'Documentos', gradient: 'gradient-text-blue' },
  '/procuradores': { label: 'Procuradores', gradient: 'gradient-text-cyan' },
  '/agenda': { label: 'Calendario', gradient: 'gradient-text-green' },
  '/tasks': { label: 'Tareas', gradient: 'gradient-text-coral' },
  '/deadlines': { label: 'Plazos', gradient: 'gradient-text-orange' },
  '/billing': { label: 'Facturación', gradient: 'gradient-text-rose' },
  '/billing/pending': { label: 'Pendientes de facturar', gradient: 'gradient-text-rose' },
  '/accounting': { label: 'Contabilidad', gradient: 'gradient-text-rose' },
  '/ctb': { label: 'Contabilidad', gradient: 'gradient-text-rose' },
  '/rates': { label: 'Tarifas', gradient: 'gradient-text-purple' },
  '/ai': { label: 'Asistente IA', gradient: 'gradient-text-purple' },
  '/ocr': { label: 'OCR', gradient: 'gradient-text-cyan' },
  '/settings': { label: 'Ajustes', gradient: 't-primary' },
}

const roleLabel: Record<string, string> = {
  ADMIN: 'Administrador',
  ABOGADO: 'Abogado',
  PARALEGAL: 'Paralegal',
  LECTURA: 'Solo lectura',
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

interface Props {
  onChangePassword: () => void
}

export default function Topbar({ onChangePassword }: Props) {
  const { pathname } = useLocation()
  const { theme, toggle } = useTheme()
  const auth = useAuth()
  const navigate = useNavigate()
  const isLight = theme === 'light'
  const key = pathname.startsWith('/settings')
    ? '/settings'
    : pathname.startsWith('/accounting')
      ? '/accounting'
      : pathname.startsWith('/ctb')
        ? '/ctb'
        : pathname
  const t = titleMap[key] ?? { label: 'Toga', gradient: 't-primary' }

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    if (menuOpen) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [menuOpen])

  const user = auth.status === 'authenticated' ? auth.user : null

  const logout = async () => {
    await auth.logout()
    navigate('/login', { replace: true })
  }

  return (
    <header
      className="sticky top-0 z-20 h-16 flex items-center justify-between px-8 backdrop-blur border-b"
      style={{ background: 'var(--bg-topbar)', borderColor: 'var(--border)' }}
    >
      <h1 className={`text-xl font-bold ${t.gradient}`}>{t.label}</h1>
      <div className="flex items-center gap-3">
        <GlobalSearch isLight={isLight} />
        <button
          type="button"
          onClick={toggle}
          className={[
            'w-9 h-9 grid place-items-center rounded-full transition',
            isLight
              ? 'hover:bg-slate-200/60 text-indigo-500'
              : 'hover:bg-slate-800/60 text-amber-300',
          ].join(' ')}
          aria-label="Cambiar tema"
        >
          {isLight ? <Moon size={18} /> : <Sun size={18} />}
        </button>
        <button
          type="button"
          className={[
            'w-9 h-9 grid place-items-center rounded-full transition',
            isLight
              ? 'hover:bg-slate-200/60 text-slate-500'
              : 'hover:bg-slate-800/60 text-slate-300',
          ].join(' ')}
          aria-label="Notificaciones"
        >
          <Bell size={18} />
        </button>

        {user && (
          <div className="relative" ref={menuRef}>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={[
                'flex items-center gap-2 pl-1 pr-2 py-1 rounded-full transition',
                isLight ? 'hover:bg-slate-200/60' : 'hover:bg-slate-800/60',
              ].join(' ')}
            >
              <span className="w-8 h-8 rounded-full grid place-items-center bg-gradient-to-br from-toga-600 to-toga-800 text-white text-xs font-semibold">
                {initials(user.fullName)}
              </span>
              <ChevronDown size={14} className={isLight ? 'text-slate-500' : 'text-slate-400'} />
            </button>

            {menuOpen && (
              <div
                className="absolute right-0 mt-2 w-64 surface-deep p-2 shadow-xl animate-slideUp"
                style={{ background: 'var(--bg-surface-strong)' }}
              >
                <div className="px-3 py-2 border-b border-base mb-1">
                  <div className="text-sm font-semibold t-primary truncate">{user.fullName}</div>
                  <div className="text-xs t-muted truncate">{user.email}</div>
                  <div className="text-[11px] uppercase tracking-wider text-toga-300 mt-1">
                    {roleLabel[user.role]}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    navigate('/settings')
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm t-secondary hover:bg-hover"
                >
                  <UserCircle size={16} /> Mi perfil
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    onChangePassword()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm t-secondary hover:bg-hover"
                >
                  <KeyRound size={16} /> Cambiar contraseña
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    void logout()
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-rose-400 hover:bg-rose-500/10"
                >
                  <LogOut size={16} /> Cerrar sesión
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  )
}
